import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MonitorIcon, LayoutIcon, CodeIcon, SmartphoneIcon, ArrowRightIcon, GiftIcon, TrendingUpIcon, MousePointer2Icon, SearchIcon, BarChart3Icon, ZapIcon, ClockIcon, UsersIcon, ClipboardCheckIcon, PenToolIcon, RocketIcon, HeadphonesIcon, ImagePlusIcon, ServerIcon, ShieldCheckIcon, RefreshCwIcon, HelpCircleIcon, ChevronDownIcon } from 'lucide-react';
const growthFeatures = [{
  icon: MousePointer2Icon,
  title: 'Lead Capture',
  desc: 'Strategically placed forms and CTAs to convert visitors into leads.'
}, {
  icon: SearchIcon,
  title: 'SEO Optimized',
  desc: 'Built-in search engine optimization to help your business get found.'
}, {
  icon: BarChart3Icon,
  title: 'Analytics',
  desc: 'Full tracking of visitor behavior and conversion metrics.'
}, {
  icon: ZapIcon,
  title: 'CRM Integration',
  desc: 'Sync leads directly with your existing sales and marketing tools.'
}];
const whyChooseUs = [{
  icon: MonitorIcon,
  title: 'Telecom Expertise',
  desc: 'We understand your industry and what your customers are looking for.'
}, {
  icon: ClockIcon,
  title: 'Fast Delivery',
  desc: 'Professional websites launched in weeks, not months.'
}, {
  icon: UsersIcon,
  title: 'Partner Focused',
  desc: 'We grow when you grow. Our success is tied to your business growth.'
}];
const processSteps = [{
  icon: ClipboardCheckIcon,
  title: 'Discovery',
  desc: 'We learn about your business goals and target audience.'
}, {
  icon: PenToolIcon,
  title: 'Design',
  desc: 'Custom layout design focused on your brand identity.'
}, {
  icon: RocketIcon,
  title: 'Launch',
  desc: 'Final testing and deployment to your custom domain.'
}, {
  icon: HeadphonesIcon,
  title: 'Support',
  desc: 'Ongoing maintenance and technical updates.'
}];
export function WebsiteDesignPage({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  return <div className="bg-white" data-id="element-2067">
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden bg-slate-900" data-id="element-2068">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-2069"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/90" data-id="element-2070"></div>
        <div className="absolute top-0 right-1/4 -translate-y-1/4 w-[600px] h-[600px] bg-pink-500/10 rounded-full blur-[120px] pointer-events-none" data-id="element-2071"></div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2072">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-2073">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-medium mb-6 backdrop-blur-sm" data-id="element-2074">
              <GiftIcon className="w-4 h-4 mr-2 text-pink-400" data-id="element-2075" />
              Exclusive Telecom Customer Perk
            </div>
            {/* Modified Heading */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-4 tracking-tight" data-id="element-2076">
              Free High-End
              <br className="hidden md:block" data-id="element-2077" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-dark" data-id="element-2078">
                Website Design
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto leading-relaxed mb-10" data-id="element-2079">
              We don't just connect your business; we help it grow. Get a
              professional, lead-generating website at zero design cost when you
              bundle with our telecom services.
            </p>
            {/* Modified Button */}
            <button onClick={() => onNavigate('home')} className="bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center" data-id="element-2080">
              Claim Your Free Site
              <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-2081" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Value Prop Section */}
      <section className="py-24 bg-slate-50" data-id="element-2082">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2083">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-2084">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-2085">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-2086">
                Why are we giving away free websites?
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6" data-id="element-2087">
                At Ambient Telecom, our goal is to help your business thrive. We
                know that a strong online presence is just as critical as
                reliable internet and phone systems.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-8" data-id="element-2088">
                By offering free, high-quality website design to our telecom
                customers, we help you generate more leads, close more sales,
                and grow your bottom line. When your business grows, our
                partnership grows.
              </p>

              <ul className="space-y-4" data-id="element-2089">
                {['Professional, modern design', 'Mobile-responsive layouts', 'SEO-optimized structure', 'Lead generation forms included'].map((item, i) => <li key={i} className="flex items-center text-slate-700 font-medium" data-id="element-2090">
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mr-3" data-id="element-2091">
                      <div className="w-2 h-2 bg-green-500 rounded-full" data-id="element-2092"></div>
                    </div>
                    {item}
                  </li>)}
              </ul>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-2093">
              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm" data-id="element-2094">
                <MonitorIcon className="w-10 h-10 text-primary mb-4" data-id="element-2095" />
                <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-2096">
                  Custom Design
                </h3>
                <p className="text-slate-600" data-id="element-2097">
                  Tailored to your brand identity and business goals.
                </p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm mt-0 sm:mt-8" data-id="element-2098">
                <SmartphoneIcon className="w-10 h-10 text-primary mb-4" data-id="element-2099" />
                <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-2100">
                  Mobile Ready
                </h3>
                <p className="text-slate-600" data-id="element-2101">
                  Looks and functions perfectly on all devices.
                </p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm" data-id="element-2102">
                <TrendingUpIcon className="w-10 h-10 text-primary mb-4" data-id="element-2103" />
                <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-2104">
                  Lead Focused
                </h3>
                <p className="text-slate-600" data-id="element-2105">
                  Designed specifically to convert visitors into customers.
                </p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm mt-0 sm:mt-8" data-id="element-2106">
                <CodeIcon className="w-10 h-10 text-primary mb-4" data-id="element-2107" />
                <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-2108">
                  Fast & Secure
                </h3>
                <p className="text-slate-600" data-id="element-2109">
                  Built on modern frameworks for speed and reliability.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Growth Features */}
      <section className="py-24 bg-white" data-id="element-2110">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2111">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2112">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-2113">
              Built for Growth
            </h2>
            <p className="text-lg text-slate-600" data-id="element-2114">
              Our websites are more than just digital business cards—they are
              powerful sales tools.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-2115">
            {growthFeatures.map((feature, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="p-8 rounded-2xl border border-slate-100 bg-slate-50 hover:bg-white hover:shadow-xl transition-all" data-id="element-2116">
                <feature.icon className="w-10 h-10 text-primary mb-6" data-id="element-2117" />
                <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-2118">
                  {feature.title}
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed" data-id="element-2119">
                  {feature.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Portfolio Showcase */}
      <section className="py-24 bg-slate-50" data-id="element-2120">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2121">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2122">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-2123">
              Recent Transformations
            </h2>
            <p className="text-lg text-slate-600" data-id="element-2124">
              See how a modern web presence drives real business results.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2125">
            {[{
            industry: 'Local Plumber',
            result: '+150% Online Leads',
            desc: 'Replaced an outdated 10-year-old site with a mobile-first design featuring click-to-call buttons and an emergency service form.',
            color: 'bg-blue-500'
          }, {
            industry: 'Law Firm',
            result: 'Top 3 Local Ranking',
            desc: 'Built an SEO-optimized site with dedicated practice area pages, resulting in a massive boost in local search visibility.',
            color: 'bg-slate-800'
          }, {
            industry: 'Medical Clinic',
            result: 'Streamlined Booking',
            desc: 'Integrated a secure patient portal and online appointment scheduling, reducing front-desk call volume by 40%.',
            color: 'bg-teal-500'
          }].map((project, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-white rounded-3xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-xl transition-shadow" data-id="element-2126">
                <div className={`h-40 ${project.color} flex items-center justify-center relative overflow-hidden`} data-id="element-2127">
                  <div className="absolute inset-0 bg-black/10" data-id="element-2128"></div>
                  <LayoutIcon className="w-16 h-16 text-white/50" data-id="element-2129" />
                </div>
                <div className="p-8" data-id="element-2130">
                  <div className="text-sm font-bold text-primary uppercase tracking-wider mb-2" data-id="element-2131">
                    {project.industry}
                  </div>
                  <h3 className="text-2xl font-black text-slate-900 mb-4" data-id="element-2132">
                    {project.result}
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-2133">
                    {project.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-slate-900 text-white" data-id="element-2134">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2135">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-2136">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-2137">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-2138">
                Why Partner With Us for Web Design?
              </h2>
              <p className="text-slate-400 text-lg mb-8" data-id="element-2139">
                Most web agencies don't understand the telecom landscape. We do.
                We build websites that speak directly to your customers' needs.
              </p>
              <div className="space-y-6" data-id="element-2140">
                {whyChooseUs.map((item, i) => <div key={i} className="flex gap-4" data-id="element-2141">
                    <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center shrink-0" data-id="element-2142">
                      <item.icon className="w-6 h-6 text-primary" data-id="element-2143" />
                    </div>
                    <div data-id="element-2144">
                      <h4 className="font-bold text-white" data-id="element-2145">{item.title}</h4>
                      <p className="text-sm text-slate-400" data-id="element-2146">{item.desc}</p>
                    </div>
                  </div>)}
              </div>
            </motion.div>
            <div className="bg-white/5 p-10 rounded-3xl border border-white/10" data-id="element-2147">
              <h3 className="text-2xl font-bold mb-6" data-id="element-2148">
                Lead Generation Machine
              </h3>
              <p className="text-slate-400 mb-8" data-id="element-2149">
                Our designs are focused on one thing: conversion. We use proven
                psychological triggers and UX patterns to guide visitors toward
                your contact forms.
              </p>
              <div className="p-6 bg-primary/10 rounded-2xl border border-primary/20" data-id="element-2150">
                <p className="text-primary font-bold text-lg mb-2" data-id="element-2151">
                  Average Results:
                </p>
                <div className="space-y-2" data-id="element-2152">
                  <div className="flex justify-between" data-id="element-2153">
                    <span className="text-slate-300" data-id="element-2154">Conversion Rate</span>
                    <span className="text-white font-bold" data-id="element-2155">+45%</span>
                  </div>
                  <div className="flex justify-between" data-id="element-2156">
                    <span className="text-slate-300" data-id="element-2157">Mobile Traffic</span>
                    <span className="text-white font-bold" data-id="element-2158">+60%</span>
                  </div>
                  <div className="flex justify-between" data-id="element-2159">
                    <span className="text-slate-300" data-id="element-2160">Page Load Speed</span>
                    <span className="text-white font-bold" data-id="element-2161">&lt; 2s</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-24 bg-white" data-id="element-2162">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2163">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2164">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-2165">
              Our Design Process
            </h2>
            <p className="text-lg text-slate-600" data-id="element-2166">
              We make getting a professional website simple and stress-free.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8" data-id="element-2167">
            {processSteps.map((step, i) => <div key={i} className="text-center" data-id="element-2168">
                <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-2169">
                  <step.icon className="w-8 h-8 text-primary" data-id="element-2170" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-2171">
                  {step.title}
                </h3>
                <p className="text-slate-500 text-sm" data-id="element-2172">{step.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Hosting & Maintenance */}
      <section className="py-24 bg-slate-900 text-white relative overflow-hidden" data-id="element-2173">
        <div className="absolute top-0 right-0 w-96 h-96 bg-pink-500/10 rounded-full blur-[100px]" data-id="element-2174"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-2175">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2176">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-2177">
              Beyond the Launch
            </h2>
            <p className="text-lg text-slate-400" data-id="element-2178">
              A website is never truly "finished." We provide ongoing hosting
              and maintenance to keep your site fast, secure, and up-to-date.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2179">
            {[{
            icon: ServerIcon,
            title: 'Cloud Hosting',
            desc: 'Lightning-fast, reliable hosting on premium cloud servers.'
          }, {
            icon: ShieldCheckIcon,
            title: 'SSL Security',
            desc: 'Free SSL certificates to keep visitor data encrypted and secure.'
          }, {
            icon: RefreshCwIcon,
            title: 'Daily Backups',
            desc: 'Automatic daily backups so you never lose your content.'
          }, {
            icon: CodeIcon,
            title: 'Core Updates',
            desc: 'We handle all technical updates to plugins and frameworks.'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center" data-id="element-2180">
                <item.icon className="w-10 h-10 text-pink-400 mx-auto mb-4" data-id="element-2181" />
                <h3 className="text-xl font-bold mb-3" data-id="element-2182">{item.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed" data-id="element-2183">
                  {item.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Web Design FAQ */}
      <section className="py-24 bg-slate-50" data-id="element-2184">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2185">
          <div className="text-center mb-16" data-id="element-2186">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 flex items-center justify-center gap-3" data-id="element-2187">
              <HelpCircleIcon className="w-8 h-8 text-pink-500" data-id="element-2188" />
              Web Design FAQ
            </h2>
            <p className="text-lg text-slate-600" data-id="element-2189">
              Common questions about our free website program.
            </p>
          </div>
          <div className="space-y-4" data-id="element-2190">
            {[{
            q: 'Do I own the website?',
            a: 'Yes! Once the site is launched, you own all the content and design. If you ever decide to leave our hosting, you can take the website with you.'
          }, {
            q: 'How long does it take to build?',
            a: 'Most standard business websites are designed, developed, and launched within 3 to 4 weeks, provided we have all the necessary content and approvals from you.'
          }, {
            q: 'Will it work on mobile phones?',
            a: 'Absolutely. Over 60% of web traffic is mobile. Every site we build uses responsive design to ensure it looks perfect on phones, tablets, and desktops.'
          }, {
            q: 'Can I update the website myself?',
            a: "Yes. We build on user-friendly Content Management Systems (CMS). We'll provide training so you can easily update text, swap images, or add blog posts without knowing how to code."
          }, {
            q: 'What if I need structural changes later?',
            a: 'Our ongoing maintenance packages include minor monthly updates. For larger structural changes or new page additions, we offer discounted hourly rates for our telecom partners.'
          }].map((faq, i) => <div key={i} className="border border-slate-200 rounded-2xl overflow-hidden bg-white" data-id="element-2191">
                <button onClick={() => setActiveFaq(activeFaq === i ? null : i)} className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors" data-id="element-2192">
                  <span className="font-bold text-slate-900" data-id="element-2193">{faq.q}</span>
                  <motion.div animate={{
                rotate: activeFaq === i ? 180 : 0
              }} data-id="element-2194">
                    <ChevronDownIcon className="w-5 h-5 text-slate-400" data-id="element-2195" />
                  </motion.div>
                </button>
                <AnimatePresence data-id="element-2196">
                  {activeFaq === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-2197">
                      <div className="p-6 pt-0 text-slate-600 border-t border-slate-100" data-id="element-2198">
                        {faq.a}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary py-20 relative overflow-hidden" data-id="element-2199">
        <div className="absolute inset-0 bg-grid-pattern opacity-10" data-id="element-2200"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center" data-id="element-2201">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-2202">
            Upgrade your telecom. Get a free website.
          </h2>
          <p className="text-xl text-blue-100 mb-10" data-id="element-2203">
            Contact us to learn more about our telecom bundles and how you can
            qualify for a free custom website design.
          </p>
          {/* Modified Button */}
          <button onClick={() => onNavigate('contact')} className="bg-white text-primary px-10 py-4 rounded-full font-bold text-lg hover:bg-slate-50 transition-all shadow-xl inline-flex items-center" data-id="element-2204">
            Let's Talk Growth
            <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-2205" />
          </button>
        </div>
      </section>
    </div>;
}